import React, { useState, useCallback, useEffect } from 'react';
import uploadIcon from '../../assets/botTrainingIcons/uploadIcon.svg';
import deleteIcon from '../../assets/botTrainingIcons/deleteIcon.svg';
import pdfIcon from '../../assets/botTrainingIcons/pdfIcon.svg';
import searchIcon from '../../assets/botTrainingIcons/searchIcon.svg';
import moreOptionIcon from '../../assets/botTrainingIcons/moreOptionIcon.svg';
import { DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import config from '../../config';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';

export default function FilesComponent() {
  const { t } = useTranslation();
  const filesT = key => t(`botTrainingPage.filesSection.${key}`);

  const [characters, setCharacters] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [tableDataStatus, setTableDataStatus] = useState(filesT('loadingData'));

  const [filelist, setFileList] = useState([]);
  const [openRowIndex, setOpenRowIndex] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredFiles = filelist.filter(f =>
    f.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSearch = useCallback(e => {
    setSearchTerm(e.target.value);
  }, []);

  const handleMoreOptionsClick = idx => {
    setOpenRowIndex(openRowIndex === idx ? null : idx);
  };

  const handleDragEnter = e => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = e => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped?.type === 'application/pdf') setFile(dropped);
  };

  const handleFileInput = e => {
    const selected = e.target.files[0];
    if (selected?.type !== 'application/pdf') {
      message.error(filesT('unsupportedFile'));
      return;
    }
    setFile(selected);
    // mock char count
    const mock = Math.floor(Math.random() * 10000);
    setCharacters(mock);
    message.success(filesT('uploadingSuccess'));
  };

  const handleDeleteLocal = () => {
    setFile(null);
    setCharacters(0);
  };

  const handleUploadAndTrainBtn = async () => {
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(`${config.apiUrl}upload/`, form, {
        headers: {
          Authorization: `Token ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      message.success(filesT('uploadingSuccess'));
      setFile(null);
      getScrapedFileList();
    } catch {
      message.error(filesT('uploadFailed'));
    } finally {
      setUploading(false);
    }
  };

  const getScrapedFileList = () => {
    setTableDataStatus(filesT('loadingData'));
    const user = localStorage.getItem('username');
    fetch(`${config.apiUrl}files/?user=${user}`, {
      headers: { Authorization: `Token ${localStorage.getItem('token')}` },
    })
      .then(r => r.json())
      .then(data => {
        if (!data.length) {
          setTableDataStatus(filesT('noData'));
          setFileList([]);
          return;
        }
        const refined = data.map(item => {
          let name = item.fileName.split('/').pop().split('?')[0];
          return {
            id: item.id,
            name,
            updated: formatDate(item.date),
            status: filesT('trained')
          };
        });
        setFileList(refined);
      })
      .catch(() => {
        setTableDataStatus(filesT('noData'));
      });
  };

  useEffect(() => {
    getScrapedFileList();
  }, []);

  const formatDate = iso => {
    const d = new Date(iso), now = new Date(), diff = now - d;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    if (days === 0) return filesT('today') || 'Today';
    if (days === 1) return filesT('yesterday') || 'Yesterday';
    return `${days} ${t('daysAgo', { count: days })}`;
  };

  const handleDeleteFileOption = file => {
    fetch(`${config.apiUrl}delete/${file.id}`, {
      method: 'DELETE',
    })
      .then(() => {
        message.success(filesT('deletingSuccess'));
        getScrapedFileList();
        setOpenRowIndex(null);
      })
      .catch(() => message.error(filesT('uploadFailed')));
  };

  return (
    <>
      <h2 style={{
        fontSize: 22, fontWeight: 'bold',
        marginLeft: '1.5rem', marginTop: '2rem'
      }}>
        {filesT('uploadTitle')}
      </h2>

      <div className="flex">
        {/* Upload Area */}
        <div className="links-content space-y-6">
          <div
            onDragEnter={handleDragEnter}
            onDragOver={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => document.getElementById('fileInput').click()}
            style={{
              border: '2px solid #eee', borderRadius: 6, padding: '1.5rem',
              textAlign: 'center', backgroundColor: isDragging ? '#e0e0e0' : '#fff',
              cursor: 'pointer', width: '100%', height: 200
            }}
          >
            <input
              id="fileInput"
              type="file"
              style={{ display: 'none' }}
              accept=".pdf"
              onChange={handleFileInput}
            />
            <img src={uploadIcon} alt="upload" style={{ marginBottom: 10 }} />
            <p style={{ margin: 0, fontWeight: 'bold' }}>
              {filesT('uploadInstruction')}
            </p>
            <p style={{ margin: 0, fontSize: 14, color: '#666' }}>
              {filesT('uploadSubtext')}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="train-container">
          <h2 className="train-title">{filesT('source')}</h2>
          <p className="train-link-info">
            ({characters.toLocaleString()}{' '}
            {filesT('charactersDetected')})
          </p>
          <h3 className="train-sub-title">{filesT('totalCharacters')}</h3>
          <p className="train-limit-info">
            <span style={{ fontWeight: 'bold' }}>{characters.toLocaleString()}</span>{' '}
            / {(400000).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Uploaded file preview */}
      {file && (
        <>
          <div style={{
            border: '1px solid #e0e0e0',
            borderRadius: 8, padding: 15,
            display: 'flex', alignItems: 'center', margin: '1rem', background: '#fff'
          }}>
            <img src={pdfIcon} alt="pdf" style={{ width: 40, marginRight: 15 }} />
            <div style={{ flex: 1 }}>
              <p style={{ margin: 0, fontWeight: 'bold' }}>{file.name}</p>
              <p style={{ margin: 0, fontSize: 14, color: '#666' }}>
                {(file.size / (1024 * 1024)).toFixed(2)} MB
              </p>
            </div>
            <button onClick={handleDeleteLocal} style={{ background: 'none', border: 'none' }}>
              <img src={deleteIcon} alt="del" style={{ width: 20 }} />
            </button>
          </div>
          <button
            className="upload-train-button"
            onClick={handleUploadAndTrainBtn}
            disabled={uploading}
          >
            {uploading ? filesT('uploading') : filesT('uploadAndTrain')}
          </button>
        </>
      )}

      {/* Files List */}
      <div className="filelist-container">
        <div className="filelist-header">
          <h2 className="filelist-header-title">{filesT('filesTitle')}</h2>
          <div className="filelist-search-container">
            <input
              type="text"
              placeholder={filesT('searchPlaceholder')}
              className="filelist-search-input"
              value={searchTerm}
              onChange={handleSearch}
            />
            <img src={searchIcon} alt="search" className="filelist-search-icon" />
          </div>
        </div>
        <table className="filelist-table">
          <thead>
            <tr className="filelist-table-header">
              <th>{filesT('name')}</th>
              <th>{filesT('status')}</th>
              <th>{filesT('updated')}</th>
              <th width="100"></th>
            </tr>
          </thead>
          <tbody>
            {filteredFiles.length > 0 ? (
              filteredFiles.map((f, i) => (
                <tr key={i} className="filelist-table-row">
                  <td className="filelist-table-cell">{f.name}</td>
                  <td className="filelist-table-cell">
                    <span className={`filelist-status`}>
                      {f.status}
                    </span>
                  </td>
                  <td className="filelist-table-cell">{f.updated}</td>
                  <td className="filelist-more-option">
                    <img
                      src={moreOptionIcon}
                      alt="more"
                      style={{ width: 20, cursor: 'pointer' }}
                      onClick={() => handleMoreOptionsClick(i)}
                    />
                    {openRowIndex === i && (
                      <div className="options-menu">
                        <div
                          className="option-item"
                          onClick={() => handleDeleteFileOption(f)}
                        >
                          <DeleteOutlined /> {filesT('delete')}
                        </div>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="text-center text-gray-500">
                  {tableDataStatus}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
